import { useEffect, useState, useRef, useCallback } from "react";
import socketIOClient, { Socket } from "socket.io-client";
import * as authUtil from "../../../utils/auth.util";
import { API } from "../../../config/API/api.config";

const SOCKET_SERVER_URL = API.socketUrl;

// Singleton socket instance for efficient connection management
let sharedSocket: Socket | null = null;
let socketRefCount = 0;

const getSharedSocket = (): Socket => {
  if (!sharedSocket || sharedSocket.disconnected) {
    // Check both regular token and admin token
    const token = authUtil.getToken() || authUtil.getAdminToken();

    // Prevent socket connection without valid token
    if (!token) {
      throw new Error("Authentication token is required for chat connection");
    }

    sharedSocket = socketIOClient(SOCKET_SERVER_URL, {
      extraHeaders: { "x-auth-token": token },
      auth: {
        token: token,
      },
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
    });
  }
  socketRefCount++;
  return sharedSocket;
};

const releaseSharedSocket = (): void => {
  socketRefCount--;
  if (socketRefCount <= 0 && sharedSocket) {
    sharedSocket.disconnect();
    sharedSocket = null;
    socketRefCount = 0;
  }
};

const UseChat = (groupId: string) => {
  const [messages, setMessages] = useState<any>([]);
  const [loader, setLoader] = useState(false);
  const [isJoined, setIsJoined] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const socketRef = useRef<Socket | null>(null);
  const currentRoomRef = useRef<string | null>(null);

  useEffect(() => {
    // Get shared socket instance
    try {
      socketRef.current = getSharedSocket();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to establish connection. Please log in again.");
      setLoader(false);
      return;
    }

    const socket = socketRef.current;

    // Reset state for new groupId
    setIsJoined(false);
    setError(null);
    setMessages([]);

    // Leave previous room if any
    if (currentRoomRef.current && currentRoomRef.current !== groupId) {
      socket.emit("leave-room", { id: currentRoomRef.current });
    }

    if (groupId) {
      setLoader(true);
      currentRoomRef.current = groupId;

      // Join the new room
      socket.emit("join-room-chatbot", { id: groupId });

      // Handle successful join
      const handleJoinSuccess = (event: any) => {
        if (event?.success) {
          setIsJoined(true);
          setError(null);
        } else {
          setError(event?.message || "Failed to join room");
          setIsJoined(false);
        }
      };

      // Handle chat history
      const handleChatHistory = (event: any) => {
        setMessages(event?.data || []);
        setLoader(false);
      };

      // Handle new messages
      const handleServerData = (event: any) => {
        if (event?.success === false) {
          setError(event?.message || "Message failed");
          return;
        }
        setMessages((prev: any) => [...prev, event]);
      };

      // Handle errors
      const handleError = (event: any) => {
        setError(typeof event === "string" ? event : event?.message || "Socket error");
        setLoader(false);
      };

      // Handle connection errors
      const handleConnectError = (err: Error) => {
        setError("Connection failed. Please try again.");
        setLoader(false);
        setIsJoined(false);
      };

      // Handle disconnection
      const handleDisconnect = (reason: string) => {
        setIsJoined(false);
        if (reason === "io server disconnect") {
          // Server disconnected, try to reconnect
          socket.connect();
        }
      };

      // Register event listeners
      socket.on("join-success-chatbot", handleJoinSuccess);
      socket.on("chatHistory-chatbot", handleChatHistory);
      socket.on("server-data", handleServerData);
      socket.on("error-data", handleError);
      socket.on("connect_error", handleConnectError);
      socket.on("disconnect", handleDisconnect);

      // Cleanup listeners on unmount or groupId change
      return () => {
        socket.off("join-success-chatbot", handleJoinSuccess);
        socket.off("chatHistory-chatbot", handleChatHistory);
        socket.off("server-data", handleServerData);
        socket.off("error-data", handleError);
        socket.off("connect_error", handleConnectError);
        socket.off("disconnect", handleDisconnect);
      };
    }

    return () => {
      // Only release socket on unmount, not on groupId change
    };
  }, [groupId]);

  // Release socket on component unmount
  useEffect(() => {
    return () => {
      releaseSharedSocket();
    };
  }, []);

  const sendMessage = useCallback(
    (messageBody: string) => {
      if (!socketRef.current || !groupId) {
        setError("Not connected");
        return;
      }

      if (!isJoined) {
        setError("Please wait for connection to complete");
        return;
      }

      // Clear any previous error on new send attempt
      setError(null);

      // Admin panel acts as chatbot, so use c-new-msg-chatbot event
      socketRef.current.emit("c-new-msg-chatbot", {
        id: groupId,
        data: messageBody,
        dataType: "text",
      });
    },
    [groupId, isJoined]
  );

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return { messages, sendMessage, loader, isJoined, error, clearError };
};

export default UseChat;
